import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { CP, CapCard, Pill, InitialAvatar, Stars, HBar } from "./ui";

export default function ExpertSearch() {
  const [skills, setSkills] = useState("python, cloud architecture, data engineering");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [minEvidence, setMinEvidence] = useState(0);
  const [onlyAvailable, setOnlyAvailable] = useState(false);
  const navigate = useNavigate();

  const search = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/capability/experts", { params: { skills } });
      setResults(data);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  useEffect(() => { search(); /* eslint-disable-line */ }, []);

  const filtered = useMemo(() => results.filter((r) =>
    r.task_evidence_count >= minEvidence && (!onlyAvailable || r.availability >= 0.5)
  ), [results, minEvidence, onlyAvailable]);

  const topSkills = useMemo(() => {
    const freq = {};
    filtered.forEach((r) => (r.matched_evidence || []).forEach((s) => { freq[s] = (freq[s] || 0) + 1; }));
    return Object.entries(freq).sort((a, b) => b[1] - a[1]).slice(0, 5)
      .map(([s, n]) => ({ skill: s, pct: Math.round((n / Math.max(filtered.length, 1)) * 100) }));
  }, [filtered]);

  return (
    <div className="space-y-4" data-testid="expert-discovery">
      <div className="flex flex-col sm:flex-row gap-3">
        <input value={skills} onChange={(e) => setSkills(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
          placeholder="Machine Learning, Python, NLP…" data-testid="experts-skills-input"
          className="flex-1 h-10 px-3 rounded-lg text-sm bg-white outline-none"
          style={{ border: `1px solid ${CP.border}`, color: CP.text }} />
        <button onClick={search} disabled={busy} data-testid="experts-search-btn"
          className="h-10 px-5 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
          style={{ background: CP.navy }}>{busy ? "Searching…" : "Search Experts"}</button>
      </div>

      <div className="text-xs" style={{ color: CP.sub }}>{filtered.length} experts found</div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-xl bg-white shadow-sm overflow-x-auto" style={{ border: `1px solid ${CP.border}` }} data-testid="experts-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider" style={{ color: CP.sub, borderBottom: `1px solid ${CP.border}` }}>
                <th className="text-left py-3 px-4">Name</th>
                <th className="text-right py-3 px-3">Skill Match</th>
                <th className="text-right py-3 px-3">Experience</th>
                <th className="text-left py-3 px-3">Reputation</th>
                <th className="text-left py-3 px-4">Availability</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.user_id} className="cursor-pointer hover:bg-slate-50" style={{ borderBottom: `1px solid ${CP.border}` }}
                  onClick={() => navigate(`/profile/${r.user_id}`)} data-testid={`expert-${r.user_id}`}>
                  <td className="py-2.5 px-4">
                    <div className="flex items-center gap-2.5">
                      <InitialAvatar name={r.name} size={32} />
                      <div>
                        <div className="font-medium" style={{ color: CP.text }}>{r.name}</div>
                        <div className="text-[11px]" style={{ color: CP.sub }}>{r.title || r.role}</div>
                      </div>
                    </div>
                  </td>
                  <td className="py-2.5 px-3 text-right font-bold" style={{ color: CP.blue }}>{r.coverage_pct}%</td>
                  <td className="py-2.5 px-3 text-right" style={{ color: CP.text }}>{r.task_evidence_count} tasks</td>
                  <td className="py-2.5 px-3"><Stars value={Math.min(5, (r.quality_score || 70) / 20)} /></td>
                  <td className="py-2.5 px-4">
                    <Pill tone={r.availability >= 0.5 ? "green" : "amber"}>{r.availability >= 0.5 ? "Available" : "Busy"}</Pill>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && !busy && (
                <tr><td colSpan={5} className="py-8 text-center text-sm" style={{ color: CP.sub }}>No matches — try broader skills.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="space-y-4">
          <CapCard title="Top Matching Skills" testid="experts-top-skills">
            <div className="space-y-2.5">
              {topSkills.map((s) => <HBar key={s.skill} label={s.skill} value={s.pct} max={100} suffix="%" color={CP.blue} />)}
              {topSkills.length === 0 && <div className="text-xs" style={{ color: CP.sub }}>Run a search to see coverage.</div>}
            </div>
          </CapCard>
          <CapCard title="Refine Search" testid="experts-refine">
            <div className="space-y-3">
              <div>
                <div className="text-xs mb-1" style={{ color: CP.sub }}>Min task evidence</div>
                <select value={minEvidence} onChange={(e) => setMinEvidence(Number(e.target.value))}
                  className="w-full h-9 px-2 rounded-lg text-sm bg-white outline-none" data-testid="experts-min-evidence"
                  style={{ border: `1px solid ${CP.border}`, color: CP.text }}>
                  {[0, 1, 3, 5].map((v) => <option key={v} value={v}>{v === 0 ? "Any" : `${v}+ tasks`}</option>)}
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: CP.text }}>
                <input type="checkbox" checked={onlyAvailable} onChange={(e) => setOnlyAvailable(e.target.checked)} data-testid="experts-available-toggle" />
                Available only
              </label>
              <button onClick={() => { setMinEvidence(0); setOnlyAvailable(false); }}
                className="text-xs font-semibold" style={{ color: CP.blue }} data-testid="experts-clear-filters">Clear all</button>
            </div>
          </CapCard>
        </div>
      </div>
    </div>
  );
}
